import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { toggleInvitePeople, invitePeople, toggleModalFeedbackThanks, clearInviteSentStatus } from '../../../redux/main/main.actions';
import { selectIsModalInvitePeopleEnabled, selectInviteSentStatus } from '../../../redux/main/main.selectors';

import { showNotify } from '../../../redux/notify/notify.actions';

import { trackEvent } from '../../../lib/analytics';

import Modal from '../Modal/Modal';
import LoaderSimple from '../../Loaders/LoaderSimple/LoaderSimple';
import './ModalInvitePeople.scss';

// Floor on loader visibility so a fast invite request doesn't flash the spinner.
// Success holds longer (mirrors ModalWorkspaceCreate) before closing the modal;
// errors only need a brief floor so the spinner doesn't blink under the toast.
const MIN_LOADING_MS = 500;
const MIN_ERROR_LOADING_MS = 200;

export const ModalInvitePeople = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const isOpen = useSelector(selectIsModalInvitePeopleEnabled);

    const dispatch = useDispatch();

    const inputRef = useRef(null);
    const [value, setValue] = useState('');

    const inviteSentStatus = useSelector(selectInviteSentStatus);
    const [sentCount, setSentCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const loadingStartedAtRef = useRef(0);

    useEffect(() => {
        if (inviteSentStatus !== 'success' && inviteSentStatus !== 'error') {
            return undefined;
        }

        // Error: the saga has already shown the toast. Keep the loader up only for
        // a brief floor (MIN_ERROR_LOADING_MS) so a fast failure doesn't blink the
        // spinner, then stop — no full success-length hold under a visible error.
        if (inviteSentStatus === 'error') {
            const remaining = Math.max(0, MIN_ERROR_LOADING_MS - (Date.now() - loadingStartedAtRef.current));
            const timer = setTimeout(() => {
                setLoading(false);
                if (inputRef.current) {
                    inputRef.current.focus();
                }
                dispatch(clearInviteSentStatus());
            }, remaining);

            return () => clearTimeout(timer);
        }

        // Success: keep the loader visible for at least MIN_LOADING_MS before
        // closing, so a fast request doesn't flash the spinner.
        const remaining = Math.max(0, MIN_LOADING_MS - (Date.now() - loadingStartedAtRef.current));
        const timer = setTimeout(() => {
            setLoading(false);
            trackEvent('app_modal_invite_people_submit_ok', {
                page: 'app',
                type: 'modals',
            });
            dispatch(toggleInvitePeople(null));
            dispatch(toggleModalFeedbackThanks('invite_people', 'main'));
            setTimeout(() => {
                setValue('');
                setSentCount(0);
            }, 300);
            dispatch(clearInviteSentStatus());
        }, remaining);

        return () => clearTimeout(timer);
    }, [inviteSentStatus]);


    const handleSave = () => {
        trackEvent('app_modal_invite_people_submit', {
            page: 'app',
            type: 'modals',
        });
        if (loading) return;
        if (!value) {
            dispatch(showNotify({ message: 'please_enter_email' }));
            if (inputRef.current) {
                inputRef.current.focus();
            }
            return;
        }
        if (!validateEmail(value)) {
            dispatch(showNotify({ message: 'invalid_email' }));
            if (inputRef.current) {
                inputRef.current.focus();
            }
            return;
        }

        if (sentCount > 20) return;

        setSentCount(sentCount + 1);
        loadingStartedAtRef.current = Date.now();
        setLoading(true);
        dispatch(invitePeople(value));

        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleCancel = () => {
        if (loading) return;
        dispatch(toggleInvitePeople(null));
        setTimeout(() => {
            setValue('');
            setSentCount(0);
            setLoading(false);
        }, 300);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        }
    };

    const handleRender = () => {
        if (isOpen && inputRef.current) {
            // inputRef.current.focus();
        }
    };

    const handleChange = (e) => {
        if (e.target.value?.length < 150) {
            setValue(e.target.value);
        }
    }

    const validateEmail = (emailValue) => {
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return regex.test(emailValue);
    };

    const handleBottomLinkClick = () => {
        dispatch(toggleInvitePeople(null));
        const url = `/settings/usage/`;
        history.push(url);
    };

    return (
        <Modal
            isOpen={isOpen}
            modalClass={`modal-invite-people`}
            onCancel={handleCancel}
            onShouldRender={handleRender}
        >
            <div className="modal-content-header">{t('m_invite_people.header')}</div>
            <div className="modal-content-body">

                <div className="form-group-invite">
                    <input
                        maxLength="150"
                        autoFocus={(window.innerWidth >= 1000)}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        inputMode="text"
                        ref={inputRef}
                        placeholder={t('m_diagram_share.email_placeholder')}
                        value={value}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                    />
                    <button disabled={loading || sentCount > 20} onClick={() => handleSave()}>{loading ? <LoaderSimple /> : t('b.invite')}</button>
                </div>

                {/* <div className='bottom-section'>
                    <div className='bottom-section-left'>
                        <svg width="25px" height="25px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 11V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V11M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V15.8C21 14.1198 21 13.2798 20.673 12.638C20.3854 12.0735 19.9265 11.6146 19.362 11.327C18.7202 11 17.8802 11 16.2 11H7.8C6.11984 11 5.27976 11 4.63803 11.327C4.07354 11.6146 3.6146 12.0735 3.32698 12.638C3 13.2798 3 14.1198 3 15.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z"></path></svg>
                        <div className='bottom-section-left-text'>
                            <span className="bottom-section-left-text-subtitle">Inviting additional members may increase your team’s license count and will be reflected in your next bill.<span className='bottom-section-left-text-link' onClick={(e) => { handleBottomLinkClick(); }}>See usage.</span>
                            </span>
                        </div>
                    </div>
                </div> */}

            </div>
        </Modal>
    );
};

export default ModalInvitePeople;
